import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Trophy, CheckCircle2, XCircle, Clock, Target, Star, Award, Medal } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import type { GrandQuizSession, GrandQuizQuestion } from '../lib/types';

interface RandomizedOption {
  text: string;
  originalIndex: number;
}

const GrandQuizPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState<GrandQuizSession | null>(null);
  const [questions, setQuestions] = useState<GrandQuizQuestion[]>([]);
  const [usedQuestionIds, setUsedQuestionIds] = useState<Set<string>>(new Set());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [randomizedOptions, setRandomizedOptions] = useState<RandomizedOption[]>([]);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationIcon, setCelebrationIcon] = useState<'confetti' | 'trophy' | 'medal'>('confetti');
  const [showSadAnimation, setShowSadAnimation] = useState(false);
  const [sadAnimal, setSadAnimal] = useState<string>('');
  const [showQuestionTransition, setShowQuestionTransition] = useState(false);
  const [transitionQuestionNumber, setTransitionQuestionNumber] = useState(0);
  const [isWaitingForNext, setIsWaitingForNext] = useState(false);
  const [totalResponseTime, setTotalResponseTime] = useState<number>(0);
  const [currentExplanation, setCurrentExplanation] = useState<string | null>(null);
  const [displayQuestionNumber, setDisplayQuestionNumber] = useState(1);

  // Ref to track if we're currently processing a question
  const isProcessingRef = useRef(false);

  // Liste d'animaux tristes avec leurs émojis et messages humoristiques
  const sadAnimals = [
    { emoji: '😿', name: 'Chat', message: 'Miaou... même mon chat fait mieux !' },
    { emoji: '🐶😢', name: 'Chien', message: 'Ouaf... mon chien est déçu !' },
    { emoji: '🐼😭', name: 'Panda', message: 'Le panda pleure dans son bambou...' },
    { emoji: '🐨😢', name: 'Koala', message: 'Le koala tombe de son eucalyptus !' },
    { emoji: '🦔😭', name: 'Hérisson', message: 'Le hérisson se roule en boule de tristesse...' },
    { emoji: '🐧😢', name: 'Pingouin', message: 'Le pingouin glisse sur ses larmes !' },
    { emoji: '🦆😭', name: 'Canard', message: 'Coin coin... le canard barbote dans ses pleurs !' },
    { emoji: '🐰😢', name: 'Lapin', message: 'Le lapin cache ses larmes dans ses oreilles...' },
    { emoji: '🦊😭', name: 'Renard', message: 'Le renard rusé n\'a pas vu venir cette réponse !' },
    { emoji: '🐻😢', name: 'Ours', message: 'L\'ours hiberne de tristesse...' }
  ];

  // Handle page visibility changes to prevent question changes when user switches tabs
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched away from tab - pause any timers or processes
        isProcessingRef.current = true;
      } else {
        // User returned to tab
        isProcessingRef.current = false;
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Prevent accidental navigation away from quiz
      if (session && session.status === 'in_progress' && !showResult) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [session, showResult]);

  useEffect(() => {
    const fetchSessionAndQuestions = async () => {
      if (!sessionId || !user) return;

      try {
        // Fetch session
        const { data: sessionData, error: sessionError } = await supabase
          .from('grand_quiz_sessions')
          .select('*')
          .eq('id', sessionId)
          .eq('user_id', user.id)
          .single();

        if (sessionError) throw sessionError;
        if (!sessionData) throw new Error('Session non trouvée');

        setSession(sessionData);

        // If session is completed or failed, redirect to results
        if (sessionData.status !== 'in_progress') {
          navigate(`/grand-quiz/${sessionId}/results`);
          return;
        }

        // Set the current question index based on session data
        setCurrentQuestionIndex(sessionData.current_question - 1);
        setDisplayQuestionNumber(sessionData.current_question);

        // Calculate total response time from existing responses
        const { data: existingResponses, error: responsesError } = await supabase
          .from('grand_quiz_responses')
          .select('response_time_ms')
          .eq('session_id', sessionId);

        if (responsesError) {
          console.error('Error fetching existing responses:', responsesError);
        } else {
          const totalTime = existingResponses?.reduce((sum, response) => 
            sum + (response.response_time_ms || 0), 0) || 0;
          setTotalResponseTime(totalTime);
        }

        // Fetch random questions for this session
        await fetchRandomQuestions(sessionData);

      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessionAndQuestions();
  }, [sessionId, user, navigate]);

  const fetchRandomQuestions = async (sessionData: GrandQuizSession) => {
    try {
      let allQuestions: GrandQuizQuestion[] = [];

      if (sessionData.matiere === 'Quiz global') {
        // Pour le quiz global, récupérer des questions de toutes les matières du Brevet
        const brevetMatieres = ['Français', 'Mathématiques', 'Histoire-Géo-EMC', 'Physique-Chimie', 'SVT (Sciences de la Vie et de la Terre)'];
        
        for (const matiereItem of brevetMatieres) {
          const questionsForMatiere = await fetchQuestionsForMatiere(sessionData.niveau, matiereItem);
          allQuestions = [...allQuestions, ...questionsForMatiere];
        }
      } else {
        // Logique existante pour une matière spécifique
        allQuestions = await fetchQuestionsForMatiere(sessionData.niveau, sessionData.matiere);
      }

      // Shuffle and select the required number of questions, avoiding duplicates
      const shuffledQuestions = shuffleArray(allQuestions);
      const uniqueQuestions = removeDuplicateQuestions(shuffledQuestions);
      const selectedQuestions = uniqueQuestions.slice(0, sessionData.total_questions);

      setQuestions(selectedQuestions);

      // Initialize randomized options for the current question
      if (selectedQuestions.length > 0) {
        const currentIndex = sessionData.current_question - 1;
        if (selectedQuestions[currentIndex]) {
          initializeRandomizedOptions(selectedQuestions[currentIndex]);
        }
      }

    } catch (err) {
      console.error('Error fetching questions:', err);
      throw err;
    }
  };

  const fetchQuestionsForMatiere = async (niveau: string, matiere: string): Promise<GrandQuizQuestion[]> => {
    // Get public fiches for the specified niveau and matiere with quiz
    const { data: fiches, error: fichesError } = await supabase
      .from('Fiches')
      .select('id, Title, sujet, matiere')
      .eq('public', true)
      .eq('niveau', niveau)
      .eq('matiere', matiere)
      .eq('quiz', true);

    if (fichesError) throw fichesError;

    if (!fiches || fiches.length === 0) {
      return [];
    }

    const ficheIds = fiches.map(f => f.id);

    // Get quiz IDs for these fiches
    const { data: quizzes, error: quizzesError } = await supabase
      .from('quiz')
      .select('id, fiche_id')
      .in('fiche_id', ficheIds)
      .eq('is_public', true);

    if (quizzesError) throw quizzesError;

    if (!quizzes || quizzes.length === 0) {
      return [];
    }

    const quizIds = quizzes.map(q => q.id);

    // Get all questions from these quizzes
    const { data: questionRows, error: questionsError } = await supabase
      .from('questions')
      .select('*')
      .in('quiz_id', quizIds);

    if (questionsError) throw questionsError;

    // Extract individual questions from the rows
    const questions: GrandQuizQuestion[] = [];
    
    if (questionRows) {
      for (const row of questionRows) {
        for (let i = 1; i <= 10; i++) {
          const questionText = row[`Question ${i}`];
          const option1 = row[`Q${i} Option 1`];
          const option2 = row[`Q${i} Option 2`];
          const option3 = row[`Q${i} Option 3`];
          const option4 = row[`Q${i} Option 4`];
          const correctAnswer = row[`Q${i} correct_answer`];
          const explanation = row[`Q${i} Explanation`];

          if (!questionText || !option1 || !correctAnswer) break;

          const options = [option1, option2, option3, option4].filter(Boolean);
          
          // Find the fiche title and matiere
          const quiz = quizzes.find(q => q.id === row.quiz_id);
          const fiche = quiz ? fiches.find(f => f.id === quiz.fiche_id) : null;
          const ficheTitle = fiche?.Title || fiche?.sujet || 'Fiche inconnue';
          const ficheMatiere = fiche?.matiere || matiere;

          questions.push({
            id: `${row.id}-${i}`,
            quiz_id: row.quiz_id,
            questionsTableId: row.id,
            question: questionText,
            options,
            correct_answer: correctAnswer,
            explanation: explanation || null,
            fiche_title: ficheTitle,
            fiche_matiere: ficheMatiere
          });
        }
      }
    }

    return questions;
  };

  const removeDuplicateQuestions = (questions: GrandQuizQuestion[]): GrandQuizQuestion[] => {
    const seen = new Set<string>();
    return questions.filter(question => {
      // Use question text as the unique identifier
      const questionKey = question.question.toLowerCase().trim();
      if (seen.has(questionKey)) {
        return false;
      }
      seen.add(questionKey);
      return true;
    });
  };

  const shuffleArray = <T extends unknown>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const initializeRandomizedOptions = (question: GrandQuizQuestion) => {
    const options = question.options.map((text, index) => ({
      text,
      originalIndex: index
    }));

    // Only randomize if it's not a true/false question
    if (options.length !== 2 || 
        !options.some(opt => opt.text.toLowerCase() === 'vrai') ||
        !options.some(opt => opt.text.toLowerCase() === 'faux')) {
      setRandomizedOptions(shuffleArray(options));
    } else {
      setRandomizedOptions(options);
    }
  };

  const handleAnswerSelect = async (randomizedIndex: number) => {
    if (showResult || isSubmitting || isProcessingRef.current || isWaitingForNext) return;
    
    console.log('🎯 handleAnswerSelect called');
    isProcessingRef.current = true;
    setIsSubmitting(true);
    
    const currentQuestion = questions[currentQuestionIndex];
    const selectedOption = randomizedOptions[randomizedIndex];
    const isAnswerCorrect = selectedOption.text === currentQuestion.correct_answer;
    
    setSelectedAnswer(randomizedIndex);
    setIsCorrect(isAnswerCorrect);
    setCurrentExplanation(currentQuestion.explanation);
    setShowResult(true);

    try {
      // Calculate response time for this question
      const responseTime = Date.now() - startTime;
      const newTotalResponseTime = totalResponseTime + responseTime;
      setTotalResponseTime(newTotalResponseTime);
      
      // Save the response
      const { error: responseError } = await supabase
        .from('grand_quiz_responses')
        .insert({
          session_id: session!.id,
          question_id: currentQuestion.questionsTableId,
          quiz_id: currentQuestion.quiz_id,
          question_text: currentQuestion.question,
          selected_answer: selectedOption.text,
          correct_answer: currentQuestion.correct_answer,
          is_correct: isAnswerCorrect,
          response_time_ms: responseTime
        });

      if (responseError) throw responseError;

      // Update session
      const newCorrectAnswers = session!.correct_answers + (isAnswerCorrect ? 1 : 0);
      const newCurrentQuestion = session!.current_question + 1;
      const isLastQuestion = newCurrentQuestion > session!.total_questions;
      
      // Calculate both percentages
      const questionsAnswered = session!.current_question; // This is the number of questions answered including current one
      const scorePercentage = (newCorrectAnswers / questionsAnswered) * 100; // Percentage of correct answers relative to answered questions
      const completionRate = (newCorrectAnswers / session!.total_questions) * 100; // Completion rate relative to total questions
      
      // Determine status: completed if last question, failed if wrong answer, otherwise in_progress
      const newStatus = isLastQuestion ? 'completed' : 'in_progress';

      const { error: sessionError } = await supabase
        .from('grand_quiz_sessions')
        .update({
          current_question: newCurrentQuestion,
          correct_answers: newCorrectAnswers,
          score_percentage: scorePercentage,
          completion_rate: completionRate,
          total_response_time_ms: newTotalResponseTime,
          status: newStatus,
          completed_at: isLastQuestion ? new Date().toISOString() : null
        })
        .eq('id', session!.id);

      if (sessionError) throw sessionError;

      // Update local session state
      setSession(prev => prev ? {
        ...prev,
        current_question: newCurrentQuestion,
        correct_answers: newCorrectAnswers,
        status: newStatus,
        completed_at: isLastQuestion ? new Date().toISOString() : null
      } : null);

    } catch (err) {
      console.error('Error saving response:', err);
      setError('Erreur lors de la sauvegarde de la réponse');
    } finally {
      setIsSubmitting(false);
      isProcessingRef.current = false;
    }
  };

  const getRandomCelebrationIcon = () => {
    const icons = ['confetti', 'trophy', 'medal'] as const;
    return icons[Math.floor(Math.random() * icons.length)];
  };

  const getRandomSadAnimal = () => {
    return sadAnimals[Math.floor(Math.random() * sadAnimals.length)];
  };

  const showQuestionNumber = (questionNum: number) => {
    setTransitionQuestionNumber(questionNum);
    setShowQuestionTransition(true);
    setTimeout(() => {
      setShowQuestionTransition(false);
    }, 1000);
  };

  const handleNext = async () => {
    if (!session) return;

    console.log('🔄 handleNext called');

    const isLastQuestion = displayQuestionNumber >= session.total_questions;
    const shouldEndQuiz = isLastQuestion;

    if (shouldEndQuiz) {
      // Navigate to results
      console.log('🏁 Navigating to results');
      navigate(`/grand-quiz/${session.id}/results`);
      return;
    }

    console.log('➡️ Moving to next question');
    
    // Show transition to next question
    showQuestionNumber(displayQuestionNumber + 1);
    
    // Reset for next question
    setShowResult(false);
    setSelectedAnswer(null);
    setCurrentExplanation(null);
    setIsWaitingForNext(false);
    
    // Update display question number and load next question
    const nextQuestionIndex = currentQuestionIndex + 1;
    setCurrentQuestionIndex(nextQuestionIndex);
    setDisplayQuestionNumber(displayQuestionNumber + 1);
    
    if (questions[nextQuestionIndex]) {
      initializeRandomizedOptions(questions[nextQuestionIndex]);
      setStartTime(Date.now());
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !session || questions.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error || 'Session non trouvée'}</p>
          <Link
            to="/grand-quiz"
            className="mt-4 inline-flex items-center text-sm text-red-600 hover:text-red-500"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Retour à la configuration
          </Link>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  // Déterminer la matière à afficher
  const getMatiereDisplay = () => {
    if (session.matiere === 'Quiz global') {
      return 'Quiz global';
    }
    return session.matiere;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow sm:rounded-lg overflow-hidden relative">
        {/* Question Transition Overlay */}
        {showQuestionTransition && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-gradient-to-r from-purple-600 to-blue-600">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-white animate-pulse">
                Question {transitionQuestionNumber}
              </h1>
            </div>
          </div>
        )}

        {/* Result Overlay */}
        {showResult && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
              <div className="mb-4">
                {isCorrect ? (
                  <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto animate-bounce" />
                ) : (
                  <XCircle className="h-16 w-16 text-red-500 mx-auto animate-pulse" />
                )}
              </div>
              
              <h2 className={`text-2xl font-bold mb-4 ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                {isCorrect ? 'Bonne réponse !' : 'Mauvaise réponse'}
              </h2>

              {currentExplanation && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg text-left">
                  <h3 className="font-semibold text-gray-900 mb-2">Explication :</h3>
                  <p className="text-gray-700 text-sm">{currentExplanation}</p>
                </div>
              )}

              <button
                onClick={handleNext}
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {displayQuestionNumber >= session.total_questions
                  ? 'Voir les résultats'
                  : `Prochaine question : numéro ${displayQuestionNumber + 1}`
                }
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="px-4 py-3 sm:px-6 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-white">Grand Quiz</h1>
              <p className="text-purple-100 text-sm">{session.niveau} - {getMatiereDisplay()}</p>
            </div>
            <div className="text-right">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg px-3 py-2 shadow-lg border-2 border-white/20">
                <p className="text-white font-extrabold text-base tracking-wide">
                  🏆 Votre score actuel
                </p>
                <p className="text-yellow-200 font-black text-xl mt-0.5">
                  {session.correct_answers} points
                </p>
              </div>
              <p className="text-purple-100 text-sm mt-1">
                Question {displayQuestionNumber} / {session.total_questions}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-4 py-2 bg-gray-50">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(displayQuestionNumber / session.total_questions) * 100}%`,
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-5 sm:p-6">
          <div className="mb-6">
            {/* Sujet de la fiche avec matière entre parenthèses */}
            <div className="flex items-center text-sm text-gray-500 mb-3">
              <Target className="h-4 w-4 mr-1" />
              {currentQuestion.fiche_title}
              {session.matiere === 'Quiz global' && currentQuestion.fiche_matiere && (
                <span className="ml-1">
                  ({currentQuestion.fiche_matiere})
                </span>
              )}
            </div>
            
            {/* Question principale */}
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {currentQuestion.question}
            </h3>
            
            <div className="space-y-3 relative">
              {randomizedOptions.map((option, index) => (
                <button
                  key={index}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    showResult
                      ? option.text === currentQuestion.correct_answer
                        ? 'border-green-500 bg-green-50'
                        : selectedAnswer === index
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                      : selectedAnswer === index
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 hover:border-purple-500'
                  }`}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showResult || isSubmitting}
                >
                  <div className="flex items-center justify-between">
                    <span>{option.text}</span>
                    {showResult && option.text === currentQuestion.correct_answer && (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    )}
                    {showResult && selectedAnswer === index && option.text !== currentQuestion.correct_answer && (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrandQuizPage;